/**
 * FolioThumbnail - Canvas-based thumbnail for folio preview
 * Per Constitution Section 4.1
 */
import { useEffect, useRef, memo } from 'react';
import { RotateCw, Trash2, Lock, Unlock, CheckCircle, Edit3, RectangleVertical, RectangleHorizontal } from 'lucide-react';
import { THUMBNAIL_CONSTANTS } from '../../utils/a4-constants';
import type { FolioOrientation, FolioStatus } from '../../types/folio';

export interface FolioThumbnailProps {
  /** Folio ID */
  folioId: string;
  /** Folio index (0-based) */
  folioIndex: number;
  /** Page orientation */
  orientation: FolioOrientation;
  /** Whether this folio is currently active */
  isActive: boolean;
  /** Whether this folio is locked */
  isLocked: boolean;
  /** Folio status */
  status: FolioStatus;
  /** Thumbnail image data URL (from canvas) */
  thumbnailDataUrl: string | null;
  /** Preview text content (fallback when no image) */
  previewText: string;
  /** Click handler */
  onClick: () => void;
  /** Rotate handler */
  onRotate: () => void;
  /** Delete handler */
  onDelete: () => void;
  /** Lock toggle handler */
  onToggleLock: () => void;
  /** Status toggle handler */
  onToggleStatus: () => void;
  /** Whether delete is allowed */
  canDelete: boolean;
}

/**
 * FolioThumbnail - Displays a thumbnail preview of a folio
 */
export const FolioThumbnail = memo(function FolioThumbnail({
  folioId,
  folioIndex,
  orientation,
  isActive,
  isLocked,
  status,
  thumbnailDataUrl,
  previewText,
  onClick,
  onRotate,
  onDelete,
  onToggleLock,
  onToggleStatus,
  canDelete,
}: FolioThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Get thumbnail dimensions based on orientation
  const dimensions =
    orientation === 'portrait'
      ? THUMBNAIL_CONSTANTS.PORTRAIT
      : THUMBNAIL_CONSTANTS.LANDSCAPE;

  // Draw preview text on canvas when no image
  useEffect(() => {
    if (thumbnailDataUrl || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw preview text
    if (previewText) {
      ctx.fillStyle = '#9ca3af';
      ctx.font = '8px Inter, system-ui, sans-serif';
      ctx.textBaseline = 'top';

      const padding = 6;
      const lineHeight = 10;
      const maxWidth = canvas.width - padding * 2;
      const lines = wrapText(ctx, previewText, maxWidth);

      lines.slice(0, 12).forEach((line, index) => {
        ctx.fillText(line, padding, padding + index * lineHeight);
      });

      // Add fade effect at bottom if text is truncated
      if (lines.length > 12) {
        const gradient = ctx.createLinearGradient(
          0,
          canvas.height - 30,
          0,
          canvas.height
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 1)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, canvas.height - 30, canvas.width, 30);
      }
    } else {
      // Empty page indicator
      ctx.fillStyle = '#e5e7eb';
      ctx.font = '10px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Empty', canvas.width / 2, canvas.height / 2);
    }
  }, [thumbnailDataUrl, previewText, dimensions]);

  return (
    <div
      data-folio-thumbnail={folioId}
      className={`
        group relative rounded-lg p-2 cursor-pointer transition-all duration-150
        ${
          isActive
            ? 'bg-blue-50 ring-2 ring-blue-500'
            : 'hover:bg-gray-100 ring-1 ring-transparent hover:ring-gray-300'
        }
      `}
      onClick={onClick}
    >
      {/* Thumbnail container */}
      <div
        className="relative mx-auto bg-white rounded shadow-sm border border-gray-200 overflow-hidden"
        style={{
          width: dimensions.WIDTH,
          height: dimensions.HEIGHT,
        }}
      >
        {thumbnailDataUrl ? (
          <img
            src={thumbnailDataUrl}
            alt={`Page ${folioIndex + 1}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <canvas
            ref={canvasRef}
            width={dimensions.WIDTH}
            height={dimensions.HEIGHT}
            className="w-full h-full"
          />
        )}

        {/* Lock indicator */}
        {isLocked && (
          <div className="absolute top-1 right-1 bg-yellow-100 rounded p-0.5">
            <Lock size={10} className="text-yellow-600" />
          </div>
        )}

        {/* Status indicator */}
        <div className={`absolute top-1 left-1 rounded p-0.5 ${
          status === 'validated'
            ? 'bg-green-100'
            : status === 'modified'
              ? 'bg-orange-100'
              : 'bg-gray-100'
        }`}>
          {status === 'validated' ? (
            <CheckCircle size={10} className="text-green-600" />
          ) : (
            <Edit3 size={10} className={status === 'modified' ? 'text-orange-600' : 'text-gray-400'} />
          )}
        </div>

        {/* Page number badge */}
        <div className="absolute bottom-1 right-1 bg-gray-900/70 text-white text-[10px] px-1.5 py-0.5 rounded">
          {folioIndex + 1}
        </div>
      </div>

      {/* Status and Orientation icons - compact design */}
      <div className="mt-1 flex items-center justify-center gap-1">
        {/* Status icon */}
        <span
          className={`p-0.5 rounded ${
            status === 'validated'
              ? 'bg-green-100'
              : status === 'modified'
                ? 'bg-orange-100'
                : 'bg-gray-100'
          }`}
          title={status === 'validated' ? 'Validé' : status === 'modified' ? 'Modifié' : 'Brouillon'}
        >
          {status === 'validated' ? (
            <CheckCircle size={10} className="text-green-600" />
          ) : status === 'modified' ? (
            <Edit3 size={10} className="text-orange-600" />
          ) : (
            <Edit3 size={10} className="text-gray-400" />
          )}
        </span>
        {/* Orientation icon */}
        <span
          className="p-0.5 rounded bg-gray-100"
          title={orientation === 'portrait' ? 'Portrait' : 'Paysage'}
        >
          {orientation === 'portrait' ? (
            <RectangleVertical size={10} className="text-gray-500" />
          ) : (
            <RectangleHorizontal size={10} className="text-gray-500" />
          )}
        </span>
      </div>

      {/* Hover actions */}
      <div
        className={`
          absolute top-1 right-1 flex gap-1 transition-opacity duration-150
          ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Status toggle button */}
        <button
          type="button"
          onClick={onToggleStatus}
          className={`p-1 rounded shadow-sm transition-colors ${
            status === 'validated'
              ? 'bg-green-100 hover:bg-green-200'
              : status === 'modified'
                ? 'bg-orange-100 hover:bg-orange-200'
                : 'bg-white hover:bg-gray-100'
          }`}
          title={status === 'validated' ? 'Marquer comme modifié' : 'Valider la page'}
        >
          {status === 'validated' ? (
            <CheckCircle size={12} className="text-green-600" />
          ) : (
            <Edit3 size={12} className={status === 'modified' ? 'text-orange-600' : 'text-gray-600'} />
          )}
        </button>

        <button
          type="button"
          onClick={onRotate}
          className="p-1 bg-white rounded shadow-sm hover:bg-gray-100 transition-colors"
          title="Rotate orientation"
        >
          <RotateCw size={12} className="text-gray-600" />
        </button>

        <button
          type="button"
          onClick={onToggleLock}
          className="p-1 bg-white rounded shadow-sm hover:bg-gray-100 transition-colors"
          title={isLocked ? 'Unlock folio' : 'Lock folio'}
        >
          {isLocked ? (
            <Unlock size={12} className="text-yellow-600" />
          ) : (
            <Lock size={12} className="text-gray-600" />
          )}
        </button>

        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="p-1 bg-white rounded shadow-sm hover:bg-red-50 transition-colors"
            title="Delete folio"
          >
            <Trash2 size={12} className="text-red-500" />
          </button>
        )}
      </div>
    </div>
  );
});

/**
 * Wrap text to fit within a given width
 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

export default FolioThumbnail;
